// MT Compute API service (MTWHT-CMP-01 / PNW-1764)
//
// The Compute tab shows the computed Withholding Tax output for the selected
// entity/period: PROCESSED transactions grouped and totalled per the country's
// Compute Configuration. Columns are driven by the summary-config API; data,
// totals and Excel export come from the data-management compute endpoints.
//
// All calls route through the shared MT api-utils helpers (auth-token injection,
// response unwrapping, error handling). Country context is sent via the
// X-Country-Code header (data-management) and on the path (admin config).

import {
    MT_COMPUTE_DATA_API,
    MT_COMPUTE_EXCEL_API,
    MT_COMPUTE_SUMMARY_CONFIG_API,
    MT_COMPUTE_TOTAL_SUM_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
    mtBuildRequestHeader,
    mtHandleErrorResponse,
    mtHandleRequestFailure,
    mtParseJsonResponse,
    mtUrlBuilder,
    type MTApiResponse,
} from '@/multi-territory/mt-services/mt-api-utils'
import type {
    MTComputeConfig,
    MTComputeDataResponse,
    MTComputeRequest,
    MTComputeTotalSum,
} from '@/multi-territory/mt-types/mt-compute'

/** Only PROCESSED transactions feed Compute (AC4). */
export const MT_COMPUTE_BUCKET = 'PROCESSED'

function buildComputeHeaders(countryCode: string): Record<string, string> {
  return { 'X-Country-Code': countryCode }
}

/**
 * GET the Compute summary configuration for a country — the dimensions
 * (GROUP_BY) and measures (SUM) that drive the grid columns and totals.
 */
export const fetchMTComputeConfig = async (
  countryCode: string
): Promise<MTApiResponse<MTComputeConfig>> => {
  const url = mtUrlBuilder(MT_COMPUTE_SUMMARY_CONFIG_API(countryCode))
  const options = mtBuildRequestHeader(
    'GET',
    null,
    buildComputeHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTComputeConfig>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * POST the grouped/summed computed rows for the selected filter (year + month
 * wise). Paginated. Honours searchCOList. isReportDownload must be false.
 */
export const fetchMTComputeData = async (
  countryCode: string,
  bucket: string,
  body: MTComputeRequest
): Promise<MTApiResponse<MTComputeDataResponse>> => {
  const url = mtUrlBuilder(MT_COMPUTE_DATA_API(bucket))
  const options = mtBuildRequestHeader(
    'POST',
    body,
    buildComputeHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTComputeDataResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * POST the total sum across all records for the selected filter — one number
 * per SUM (measure) column, used for the totals row shown at the top of the grid.
 */
export const fetchMTComputeTotalSum = async (
  countryCode: string,
  bucket: string,
  body: MTComputeRequest
): Promise<MTApiResponse<MTComputeTotalSum>> => {
  const url = mtUrlBuilder(MT_COMPUTE_TOTAL_SUM_API(bucket))
  const options = mtBuildRequestHeader(
    'POST',
    body,
    buildComputeHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTComputeTotalSum>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * POST an Excel export of the computed grid (same columns/labels as the screen,
 * honouring the applied search/filter). isReportDownload must be true.
 *
 * The endpoint streams the .xlsx file directly (binary, magic bytes "PK..."),
 * NOT JSON — so the response is read as a Blob and never JSON-parsed. Returns the
 * Blob (with an optional server-provided filename from Content-Disposition) so the
 * caller can trigger a browser download.
 */
export const downloadMTComputeExcel = async (
  countryCode: string,
  bucket: string,
  body: MTComputeRequest
): Promise<MTApiResponse<{ blob: Blob; filename: string }>> => {
  const url = mtUrlBuilder(MT_COMPUTE_EXCEL_API(bucket))
  const options = mtBuildRequestHeader(
    'POST',
    body,
    buildComputeHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    const blob = await response.blob()
    const filename = parseFilename(response.headers.get('content-disposition'))
    return { success: true, data: { blob, filename } }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/** Extract a filename from a Content-Disposition header, with a sensible default. */
function parseFilename(contentDisposition: string | null): string {
  const fallback = 'compute-output.xlsx'
  if (!contentDisposition) return fallback
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition)
  return match ? decodeURIComponent(match[1]) : fallback
}
